/**
 * Inline SVG icon set. Mirrors the stroke-style outline icons in the mockup
 * (mock_up_web_24052026/icons.jsx). Adding only what the player app uses;
 * shared component lib in @polf/shared/components will absorb these later
 * when admin/projector need the same icons.
 *
 * Default stroke inherits `currentColor`, sized via the `size` prop.
 */
type IconProps = { size?: number; className?: string }

const Svg = ({
  children,
  size = 22,
  className,
}: IconProps & { children: React.ReactNode }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden
  >
    {children}
  </svg>
)

export const IconFlag = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 22V4M4 4h12l-3 4 3 4H4" />
  </Svg>
)

export const IconCard = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M7 10h10M7 14h6" />
  </Svg>
)

export const IconLeaderboard = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 21h16M7 21V11M12 21V5M17 21v-7" />
  </Svg>
)

export const IconTarget = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
  </Svg>
)

export const IconUsers = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0 1 16 0" />
  </Svg>
)

export const IconPokerChip = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="4" />
    <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
  </Svg>
)

export const IconTrophy = (p: IconProps) => (
  <Svg {...p}>
    {/* Cup + two handles + stem + base — the "premier/prizes" glyph. */}
    <path d="M7 4h10v5a5 5 0 0 1-10 0V4z" />
    <path d="M7 5H4v1a3 3 0 0 0 3 3" />
    <path d="M17 5h3v1a3 3 0 0 1-3 3" />
    <path d="M12 14v3" />
    <path d="M10 21a2 2 0 0 1 4 0" />
    <path d="M8 21h8" />
  </Svg>
)

export const IconChevronLeft = (p: IconProps) => (
  <Svg {...p}>
    <path d="M15 18l-6-6 6-6" />
  </Svg>
)

export const IconBell = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 8a6 6 0 0 1 12 0v5l1.5 3h-15L6 13V8z" />
    <path d="M10 19a2 2 0 0 0 4 0" />
  </Svg>
)

export const IconMore = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="5" cy="12" r="1.4" fill="currentColor" />
    <circle cx="12" cy="12" r="1.4" fill="currentColor" />
    <circle cx="19" cy="12" r="1.4" fill="currentColor" />
  </Svg>
)

export const IconCheck = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 12l5 5L20 7" />
  </Svg>
)

/**
 * Side-game icons — distinct silhouettes per type so the banner +
 * SideGames cards read at a glance.
 */

/**
 * Closest to the pin — flag stuck in the green with proximity rings
 * radiating around the base. The rings communicate "close to" via
 * the concentric-circles visual idiom.
 */
export const IconClosest = (p: IconProps) => (
  <Svg {...p}>
    {/* Flag pole + flag */}
    <path d="M9 20V5" />
    <path d="M9 5h7l-2 2.5L16 10H9" />
    {/* Proximity rings around the base */}
    <circle cx="9" cy="20" r="2" />
    <path d="M4 20a5 5 0 0 1 10 0" />
  </Svg>
)

/**
 * Longest drive — diagonal driver with a small ball at the tee tip
 * and motion lines trailing the head, signalling distance.
 */
export const IconLongest = (p: IconProps) => (
  <Svg {...p}>
    {/* Club shaft (top-right to bottom-left) */}
    <path d="M18 4L7 17" />
    {/* Club head (driver — chunky at the bottom of the shaft) */}
    <path d="M5 19l-1.5 1.5a1 1 0 0 0 0 1.4l.1.1a1 1 0 0 0 1.4 0L6.5 20.5z" />
    {/* Ball (top-right of head, at the tee) */}
    <circle cx="20" cy="6" r="1.4" />
    {/* Distance trajectory — arc up and away */}
    <path d="M14 8c3-2 6-2 8 0" strokeDasharray="2 2" />
  </Svg>
)

/**
 * Slå proffen — crown over a flag. Champion-banner mash-up: the
 * pro's flag with the "you're the champion" cue stacked on top.
 * Reads as "claim the pro's hole."
 */
export const IconBeatPro = (p: IconProps) => (
  <Svg {...p}>
    {/* Crown — three peaks + banded base */}
    <path d="M4 9l2.5 4L9 7l3 5 3-5 2.5 6L20 9v6H4z" />
    <path d="M4 17h16" />
    {/* Pole below the crown */}
    <path d="M12 17v4" />
  </Svg>
)
